import { vec2, vec3, vec4 } from "gl-matrix";
import { BoundingBox, IBoundingBoxObject } from '@spi/Common/Util/BoundingBox';
import { calculateNormal, Ray } from '@spi/Common/Math';

export enum TraversabilityType {
    // Traversability is determined by the minTraversableHeight setting
    Natural,
    // Always considered traversable
    Open,
    // Can never be traversed
    Blocked
}

export class Face implements IBoundingBoxObject {

    private _bounds: BoundingBox;

    public tags: any;
    public vertices: Vertex[];
    public normal: vec3;
    public changed: boolean;
    public textureOffsets: Map<Vertex, vec2>;
    public center: vec3;
    public offset: number;
    public traversability: TraversabilityType;
    public stepHeightOverride: number;

    constructor() {
        this.vertices = [];
        this.traversability = TraversabilityType.Natural;
        this.stepHeightOverride = null;
        this.textureOffsets = new Map<Vertex, vec2>();
    }

    public setStepHeightOverride(stepHeight: number): Face {
        this.stepHeightOverride = stepHeight;
        return this;
    }

    public setTraversability(t: TraversabilityType): Face {
        this.traversability = t;
        return this;
    }

    public getPosition(): vec3 {
        this.calculateCenter();
        return this.center;
    }

    public setTag(tag: string, value: any) {
        if (!this.tags) {
            this.tags = {};
        }
        this.tags[tag] = value;
    }

    public getTag(tag: string): any {
        if (this.tags) {
            return this.tags[tag];
        } else {
            return undefined;
        }
    }

    public getBoundingBox(): BoundingBox {
        if (this._bounds) {
            return this._bounds;
        }

        let vs = this.vertices;

        let min = vec3.fromValues(
            Math.min(vs[0].position[0], vs[1].position[0], vs[2].position[0]),
            Math.min(vs[0].position[1], vs[1].position[1], vs[2].position[1]),
            Math.min(vs[0].position[2], vs[1].position[2], vs[2].position[2])
        );

        let max = vec3.fromValues(
            Math.max(vs[0].position[0], vs[1].position[0], vs[2].position[0]),
            Math.max(vs[0].position[1], vs[1].position[1], vs[2].position[1]),
            Math.max(vs[0].position[2], vs[1].position[2], vs[2].position[2])
        );

        this._bounds = new BoundingBox(min, max);

        return this._bounds;
    }

    public setColor(color: vec4, link?: boolean): Face {
        this.iterVertices(v => v.setColor(color, link));
        return this;
    }

    public calculateCenter(): Face {
        var center = vec3.create();
        for (var i = 0; i < this.vertices.length; i++) {
            vec3.add(center, center, this.vertices[i].position);
        }
        vec3.div(center, center, [this.vertices.length, this.vertices.length, this.vertices.length]);

        this.center = center;

        return this;
    }

    public setNormal(normal: vec3 | number[]): Face {
        this.normal = normal as vec3;
        return this;
    }

    public calculateNormal(): Face {
        this.normal = calculateNormal(this.vertices[0].position, this.vertices[1].position, this.vertices[2].position);

        return this;
    }

    public attachToVertices(): Face {
        for (var v = 0; v < this.vertices.length; v++) {
            if (this.vertices[v].partOfFaces.indexOf(this) === -1) {
                this.vertices[v].partOfFaces.push(this);
            }
        }

        return this;
    }

    public setVertices(vertexA: Vertex, vertexB: Vertex, vertexC: Vertex) {
        this.vertices = [vertexA, vertexB, vertexC];
        this.attachToVertices();

        this.calculateNormal();
        this.calculateCenter();

        this.textureOffsets = new Map<Vertex, vec2>();

        this.setTextureOffsets(vec2.create(), vec2.create(), vec2.create());

        return this;
    }

    public getClosestEdge(position: vec3): Edge {
        let distances = this.vertices.map(v => {
            return {
                distance: vec3.distance(position, v.position),
                vertex: v
            }
        });

        distances.sort(function (a, b) { return b.distance - a.distance; });

        return new Edge().setVertices(distances.pop().vertex, distances.pop().vertex);
    }

    public iterVertices(callback: (v: Vertex) => void): Face {
        this.vertices.forEach(callback);
        return this;
    }

    public markChanged(): Face {
        for (let vertex of this.vertices) {
            for (let face of vertex.partOfFaces) {
                face.changed = true;
            }
        }

        return this;
    }

    public setTextureOffsets(offsetA: vec2 | number[], offsetB: vec2 | number[], offsetC: vec2 | number[]): Face {
        let offsets = [offsetA as vec2, offsetB as vec2, offsetC as vec2];
        this.vertices.forEach((v, index) => this.textureOffsets.set(v, offsets[index]));

        return this;
    }

    public setTextureOffset(vertex: Vertex, offset: vec2 | number[]): Face {
        this.textureOffsets.set(vertex, offset as vec2);
        return this;
    }

    public getTextureOffset(vertex: Vertex): vec2 {
        return this.textureOffsets.get(vertex);
    }

    public getAdjacentFaces(): Face[] {
        let getAdjacentFaces = (vertexSet: Set<Vertex>, vertex: Vertex) => {
            let faces = new Array<Face>();

            for (let face of vertex.partOfFaces) {
                let overlapCount = 0;
                for (let v of face.vertices) {
                    if (vertexSet.has(v)) {
                        if (++overlapCount === 2) {
                            faces.push(face);
                            break;
                        }
                    }
                }
            }

            return faces;
        };

        let getFaces = (face: Face) => {
            let vertexSet = new Set<Vertex>();
            let faceSet = new Set<Face>();
            let result = new Array<Face>();

            face.vertices.forEach(v => vertexSet.add(v));

            for (let v of face.vertices) {
                let faces = getAdjacentFaces(vertexSet, v);

                for (let f of faces) {
                    if (!faceSet.has(f) && f !== face) {
                        faceSet.add(f);

                        result.push(f);
                    }
                }
            }

            return result;
        };

        return getFaces(this);
    }

    canIntersectRay(): boolean {
        return true;
    }
    intersectsRay(ray: Ray): boolean {
        return ray.getPlaneIntersection(this.vertices[0].position, this.vertices[1].position, this.vertices[2].position) != null;
    }
}

export class Vertex {

    public position: vec3;
    public color: vec4;
    public normal: vec3;
    public partOfFaces: Face[];

    constructor() {
        this.partOfFaces = [];
    }

    public getOrCreateFaceWith(vertices: Vertex[]) {
        if (vertices.length !== 2) {
            throw Error('getOrCreateFaceWith must be called with two vertices');
        }

        let face = this.getFaceWith([this, vertices[0], vertices[1]]);
        if (face) {
            return {
                face: face,
                new: false
            };
        } else {
            return {
                face: new Face().setVertices(this, vertices[0], vertices[1]),
                new: true
            };
        }
    }

    public getFaceWith(vertices: Vertex[]): Face {

        for (let face of this.partOfFaces) {
            let faceOkay = true;
            for (let vertex of vertices) {
                if (face.vertices.indexOf(vertex) === -1) {
                    faceOkay = false;
                    break;
                }
            }
            if (faceOkay) {
                return face;
            }
        }

        return null;
    }

    public setPosition(position: vec3 | number[], link?: boolean): Vertex {
        this.position = link ? position as vec3 : vec3.clone(position);

        this.partOfFaces.forEach(face => face.calculateNormal());

        return this;
    }

    public setColor(color: vec4 | number[], link?: boolean): Vertex {
        this.color = link ? color as vec4 : vec4.clone(color);
        return this;
    }

    public setNormal(normal: vec3 | number[], link?: boolean): Vertex {
        this.normal = link ? normal as vec3 : vec3.clone(normal);
        return this;
    }

    public calculateNormal(): Vertex {
        let normal = vec3.create();
        for (let face of this.partOfFaces) {
            vec3.add(normal, normal, face.normal);
        }

        this.normal = vec3.normalize(normal, normal);

        return this;
    }

    public getDistance(location): number {
        return vec3.distance(this.position, location);
    }

    public markChanged(): Vertex {
        for (let face of this.partOfFaces) {
            face.changed = true;
        }

        return this;
    }

    public getAdjacentVertices(depth: number): VertexRelation[] {
        let vertSet = new Set<Vertex>();

        let vertices: VertexRelation[] = [];
        let getVertices = (referenceVertex: Vertex, currentDepth: number) => {
            currentDepth--;
            let recurse: Vertex[] = [];
            for (let face of referenceVertex.partOfFaces) {

                for (let vert of face.vertices) {

                    if (!vertSet.has(vert)) {
                        vertSet.add(vert);

                        vertices.push({
                            vertex: vert,
                            depth: currentDepth,
                            distance: this.getDistance(referenceVertex.position)
                        });

                        if (currentDepth > 0) {
                            recurse.push(vert);
                        }
                    }
                }
            }

            recurse.forEach(vert => getVertices(vert, currentDepth));
        };

        getVertices(this, depth || 1);

        return vertices;
    };
}

export class Edge {
    public vertices: Vertex[];

    public setVertices(vertexA: Vertex, vertexB: Vertex): Edge {
        this.vertices = [vertexA, vertexB];

        return this;
    }

    public markChanged(): Edge {

        for (let vertex of this.vertices) {
            for (let face of vertex.partOfFaces) {
                face.changed = true;
            }
        }

        return this;
    }

    public iterVertices(callback: (v: Vertex) => void) {
        this.vertices.forEach(callback);
        return this;
    }
}

export type VertexRelation = {
    vertex: Vertex,
    depth: number,
    distance: number
}
